import { type ApiFetchContext, apiFetch } from "../client";
import { buildAddressListUrl } from "../path-adapter";
import type { AddressAddInput, AddressEditInput, AddressRaw, AddressVo } from "../types/address";

function normalize(raw: AddressRaw): AddressVo {
  const detail = raw.addressDetail ?? "";
  const segments = [
    raw.countryText,
    raw.provinceText,
    raw.cityText,
    raw.areaText,
    raw.townText,
    detail,
  ].filter((s): s is string => !!s && s.length > 0);
  return {
    id: raw.id,
    recipientName: raw.recipientName ?? "",
    regionCode: raw.regionCode ?? "+7",
    regionCountry: raw.regionCountry ?? "",
    mobile: raw.mobile ?? "",
    phone: raw.phone ?? "",
    postalCode: raw.postalCode ?? "",
    countryCode: raw.countryCode ?? "",
    country: raw.countryText ?? "",
    provinceCode: raw.provinceCode ?? "",
    province: raw.provinceText ?? "",
    cityCode: raw.cityCode ?? "",
    city: raw.cityText ?? "",
    districtCode: raw.districtCode ?? "",
    district: raw.areaText ?? raw.townText ?? "",
    detail,
    doorNumber: raw.doorNumber ?? "",
    consigneeCompany: raw.consigneeCompany ?? "",
    isDefault: Boolean(raw.isDefault),
    fullAddress: segments.join(" · "),
    recommendedPostalCode: raw.recommendedPostalCode ?? null,
  };
}

export async function listAddresses(ctx: ApiFetchContext, authToken: string): Promise<AddressVo[]> {
  const raw = await apiFetch<AddressRaw[]>(ctx, buildAddressListUrl(), {
    authToken,
  });
  return (raw ?? []).map(normalize);
}

export async function addAddress(
  ctx: ApiFetchContext,
  authToken: string,
  input: AddressAddInput,
): Promise<AddressVo> {
  const raw = await apiFetch<AddressRaw>(ctx, "/address/customer/add", {
    method: "POST",
    body: input,
    authToken,
  });
  return normalize(raw);
}

export async function editAddress(
  ctx: ApiFetchContext,
  authToken: string,
  input: AddressEditInput,
): Promise<string> {
  return apiFetch<string>(ctx, "/address/customer/edit", {
    method: "PUT",
    body: input,
    authToken,
  });
}

export async function setDefaultAddress(
  ctx: ApiFetchContext,
  authToken: string,
  id: string,
): Promise<string> {
  return apiFetch<string>(ctx, "/address/customer/default/set", {
    method: "PUT",
    body: { id },
    authToken,
  });
}

export async function deleteAddressesBatch(
  ctx: ApiFetchContext,
  authToken: string,
  ids: string[],
): Promise<string> {
  return apiFetch<string>(ctx, "/address/customer/deleteBatch", {
    method: "DELETE",
    body: ids,
    authToken,
  });
}

export async function getAddressDetail(
  ctx: ApiFetchContext,
  authToken: string,
  id: string,
): Promise<AddressVo> {
  const raw = await apiFetch<AddressRaw>(
    ctx,
    `/address/customer/detail?id=${encodeURIComponent(id)}`,
    { authToken },
  );
  return normalize(raw);
}

/**
 * Default transit-warehouse receiving address the
 * customer instructs the seller to ship to. Backend `GET /address/warehouse/default`
 * is anonymous/public (Shiro anon path), returns `Result<Address>`.
 *
 * Used by the confirm-order page so the warehouse block reflects the real
 * operator-configured address instead of a hardcoded string (#180).
 */
export async function getWarehouseDefaultAddress(ctx: ApiFetchContext): Promise<AddressVo> {
  const raw = await apiFetch<AddressRaw>(ctx, "/address/warehouse/default", {});
  return normalize(raw);
}
